import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ErrorOwner } from '../api/api';
import {
    approveGroup,
    approveProposals,
    Attention,
    AttentionItem,
    AttentionMail,
    AttentionOurs,
    AutoRequeueMode,
    closeIssues,
    closeMatching,
    getAttention,
    getIssueNotice,
    getIssueOverview,
    getIssues,
    getRequeueSummary,
    getSweeperStatus,
    getSweepProgress,
    getUnrecognised,
    IssueAge,
    IssueEnvironment,
    IssueGroup,
    IssueInvestigation,
    IssueList,
    IssueNotice,
    IssueRef,
    LANGUAGES,
    markNoticeSent,
    NoticeSent,
    ProposalGroup,
    rejectGroup,
    rejectProposals,
    reopenIssues,
    RequeueDecisionResult,
    RequeueGroupRef,
    RequeueSummary,
    runSweeper,
    saveAutoRequeueMode,
    saveInvestigation,
    saveScanScope,
    saveScannerPause,
    ScanMode,
    setIssueLanguage,
    stopInvestigation,
    stopSweeper,
    SweeperStatus,
    SweepProgress,
    sweepNow,
    Stage,
    UnrecognisedError,
    Issue,
} from '../api/issues';
import { LinkDialog } from '../components/Issues/LinkDialog';
import { RelinkDialog } from '../components/Issues/RelinkDialog';
import { SweeperPanel } from '../components/Issues/SweeperPanel';
import { ReevaluationPanel } from '../components/Issues/ReevaluationPanel';
import { startReevaluation } from '../api/reevaluation';
import { ProposalsPanel } from '../components/Issues/ProposalsPanel';
import { AttentionPanel } from '../components/Issues/AttentionPanel';
import { IncidentsPanel } from '../components/Issues/IncidentsPanel';
import { NoticeStylePanel } from '../components/Issues/NoticeStylePanel';
import { describeError } from '../api/requeue';
import { useIdentity } from '../auth/useIdentity';
import { useApiAuth, useAxiosSwagger } from '../hooks/useAxiosSwagger';
import { useBusy } from '../hooks/useBusy';
import { IssueBacklog } from '../components/Issues/IssueBacklog';
import { IssueNoticePanel } from '../components/Issues/IssueNoticePanel';
import { IssueOverviewTable } from '../components/Issues/IssueOverviewTable';
import { UnrecognisedPanel } from '../components/Issues/UnrecognisedPanel';
import { EnvironmentCard } from '../components/Issues/EnvironmentCard';
import { StatusFilter } from '../components/Issues/StatusFilter';
import { useCloseDialog } from '../components/Issues/CloseDialog';
import { LoadingBlock, Refreshing } from '../components/Loading/Loading';
import { OWNER_LABEL } from '../components/Interpretation/InterpretationNote';
import { environmentKey } from '../components/Issues/issueFormat';
import '../components/Issues/Issues.scss';
import '../components/Issues/Workbench.scss';

const PERIODS: Record<string, string> = {
    '30': '30 dagen',
    '90': '3 maanden',
    '180': '6 maanden',
    '365': '1 jaar',
};

/** What the status filter is called in the row of active filters. */
const STATUSES: Record<string, string> = {
    open: 'Alle open',
    resolved: 'Opgelost',
    all: 'Alles',
    WaitingOnCustomer: 'Bij klant',
    WaitingOnUs: 'Bij ons',
    NeedsRequeue: 'Opnieuw aanbieden',
    ResolvesItself: 'Gaat vanzelf',
    Closed: 'Gesloten',
};

const AGES: Record<string, string> = {
    recent: 'Recent',
    backlog: 'Achterstand',
    all: 'Alle leeftijden',
};

const DEFAULT_DAYS = '365';

/** The age limit until the first answer of the server says otherwise. */
const DEFAULT_MAX_AGE_DAYS = 30;

type Tab = 'nu' | 'overzicht' | 'issues' | 'voorstellen' | 'klantmails' | 'onbekend' | 'herbeoordeling';

/** Remembered per browser: production, test or both, for when the address bar does not say. */
const STAGE_KEY = 'issues:stage';

const readStage = (): Stage | '' => {
    try {
        const stored = window.localStorage.getItem(STAGE_KEY);

        return stored === 'prod' || stored === 'test' ? stored : '';
    } catch {
        return '';
    }
};

const writeStage = (stage: Stage | '') => {
    try {
        window.localStorage.setItem(STAGE_KEY, stage);
    } catch {
        // A private window: the switch simply starts on "alles" next time.
    }
};

const STAGES: { stage: Stage | ''; label: string }[] = [
    { stage: '', label: 'Alles' },
    { stage: 'prod', label: 'Productie' },
    { stage: 'test', label: 'Test' },
];

const TABS: { tab: Tab; label: string; icon: IconProp }[] = [
    { tab: 'nu', label: 'Nu kijken', icon: 'bell' },
    { tab: 'overzicht', label: 'Omgevingen', icon: 'table' },
    { tab: 'issues', label: 'Issues', icon: 'list-ul' },
    { tab: 'voorstellen', label: 'Opnieuw aanbieden', icon: 'rotate-left' },
    { tab: 'klantmails', label: 'Klantmails', icon: 'envelope' },
    { tab: 'onbekend', label: 'Onbekende fouten', icon: 'lightbulb' },
    { tab: 'herbeoordeling', label: 'Herbeoordeling', icon: 'scale-balanced' },
];

/** The tabs of one environment: its issues and its mail. */
const ENVIRONMENT_TABS: Tab[] = ['issues', 'klantmails'];

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('nl-NL')} ${n === 1 ? one : many}`;

/**
 * Everything that went wrong at every customer over the last year, on two levels: all
 * customers, starting at what to do now, and one environment with its issues and its mail.
 * Kept up to date by the scanner every quarter of an hour.
 *
 * The tab and every filter live in the address bar, so a reload - or a round trip through
 * Auth0 - comes back to the same view. Going to another tab or environment is a step in the
 * browser history, so Back goes back; changing a filter is not.
 */
export const Issues = () => {
    const { isAdmin, status: identity } = useIdentity();
    const { loading: tokenLoading } = useApiAuth();
    const anyAxios = useAxiosSwagger();
    /*
        Only once the token is in and the role is known. The axios instance exists before the
        token does; asking with it then sends no Authorization header, and the 401 that comes
        back stays in the error bar after the real answers arrive.
    */
    const axios = !tokenLoading && identity === 'ready' && isAdmin ? anyAxios : undefined;
    const [params, setParams] = useSearchParams();
    const { dialog: closeDialog, ask: askClose } = useCloseDialog();

    const tab = (TABS.some((t) => t.tab === params.get('tab')) ? params.get('tab') : 'nu') as Tab;
    const customer = params.get('customer') ?? '';
    const environment = params.get('environment') ?? '';
    const status = params.get('status') ?? 'open';
    const owner = (params.get('owner') ?? '') as ErrorOwner | '';
    const days = params.get('days') ?? DEFAULT_DAYS;
    const code = params.get('code');
    const q = params.get('q') ?? '';
    const stage = (params.get('stage') ?? readStage()) as Stage | '';
    // Recent by default: the backlog is there, one click away, but does not bury what is new.
    const ageParam = params.get('age') ?? 'recent';
    const age: IssueAge = ageParam === 'backlog' ? 'backlog' : ageParam === 'all' ? '' : 'recent';

    const [search, setSearch] = useState<string>(q);
    const [environments, setEnvironments] = useState<IssueEnvironment[] | null>(null);
    const [overviewLoading, setOverviewLoading] = useState<boolean>(false);
    const [list, setList] = useState<IssueList | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [sweeping, setSweeping] = useState<string | null>(null);
    const [sweepProgress, setSweepProgress] = useState<SweepProgress | null>(null);
    const sweepTarget = useRef<IssueEnvironment | null>(null);

    const [notice, setNotice] = useState<IssueNotice | null>(null);
    const [noticeAll, setNoticeAll] = useState<boolean>(false);
    const [noticeLoading, setNoticeLoading] = useState<boolean>(false);
    const [noticeError, setNoticeError] = useState<string | null>(null);
    const [marking, setMarking] = useState<boolean>(false);

    const [unrecognised, setUnrecognised] = useState<UnrecognisedError[] | null>(null);
    const [unrecognisedLoading, setUnrecognisedLoading] = useState<boolean>(false);
    const [unrecognisedError, setUnrecognisedError] = useState<string | null>(null);

    const [sweeper, setSweeper] = useState<SweeperStatus | null>(null);
    const [sweeperError, setSweeperError] = useState<string | null>(null);
    const [sweeperBusy, setSweeperBusy] = useState<boolean>(false);

    const [summary, setSummary] = useState<RequeueSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
    const [summaryError, setSummaryError] = useState<string | null>(null);

    const [attention, setAttention] = useState<Attention | null>(null);
    const [attentionLoading, setAttentionLoading] = useState<boolean>(false);
    const [attentionError, setAttentionError] = useState<string | null>(null);

    const [deciding, setDeciding] = useState<boolean>(false);
    const [reevaluating, setReevaluating] = useState<boolean>(false);
    const [relinking, setRelinking] = useState<Issue | null>(null);
    const [linking, setLinking] = useState<Issue | null>(null);
    const [decisions, setDecisions] = useState<RequeueDecisionResult[] | null>(null);
    const [decisionMessage, setDecisionMessage] = useState<string | null>(null);

    const controller = useRef<AbortController | null>(null);

    const selected = customer && environment ? environmentKey(customer, environment) : null;
    // One environment is a level of its own: its issues and its mail, with a way back to all customers.
    const inEnvironment = !!selected && ENVIRONMENT_TABS.includes(tab);
    const maxAgeDays = summary?.maxAgeDays ?? attention?.maxAgeDays ?? sweeper?.autoRequeueMaxAgeDays ?? DEFAULT_MAX_AGE_DAYS;

    // What someone waits for shows in the bar at the top, whatever is on screen.
    useBusy(overviewLoading && environments === null, 'Omgevingen ophalen');
    useBusy(loading && tab === 'issues', 'Issues ophalen');
    useBusy(summaryLoading && tab === 'voorstellen', 'Voorstellen ophalen');
    useBusy(attentionLoading && tab === 'nu', 'Uitzoeken wat er nu moet gebeuren');
    useBusy(noticeLoading, 'Klantmail opstellen');
    useBusy(unrecognisedLoading, 'Onbekende fouten verzamelen');
    useBusy(deciding, 'Bezig met je keuze');

    /** Changes the address bar; `step` makes it a step Back can go back to. */
    const update = useCallback(
        (changes: Record<string, string | null>, step = false) => {
            const next = new URLSearchParams(params);

            Object.entries(changes).forEach(([name, value]) => {
                if (value === null || value === '') {
                    next.delete(name);
                } else {
                    next.set(name, value);
                }
            });

            setParams(next, { replace: !step });
        },
        [params, setParams]
    );

    /** Goes somewhere else: a step in the history, unlike a filter. */
    const go = useCallback((changes: Record<string, string | null>) => update(changes, true), [update]);

    const loadOverview = useCallback(() => {
        if (!axios) {
            return;
        }

        setOverviewLoading(true);

        getIssueOverview(axios)
            .then(setEnvironments)
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setOverviewLoading(false));
    }, [axios]);

    const loadList = useCallback(() => {
        if (!axios || tab !== 'issues') {
            return;
        }

        controller.current?.abort();
        const current = new AbortController();
        controller.current = current;

        setLoading(true);
        setError(null);

        getIssues(
            axios,
            {
                customer: customer || undefined,
                environment: environment || undefined,
                days: Number(days),
                status,
                owner,
                code: code ?? undefined,
                q: q || undefined,
                stage,
                age,
            },
            current.signal
        )
            .then((result) => {
                if (controller.current === current) {
                    setList(result);
                }
            })
            .catch((ex) => {
                if (controller.current === current && !current.signal.aborted) {
                    setError(describeError(ex));
                }
            })
            .finally(() => {
                if (controller.current === current) {
                    controller.current = null;
                    setLoading(false);
                }
            });
    }, [axios, tab, customer, environment, days, status, owner, code, q, stage, age]);

    const loadNotice = useCallback(() => {
        if (!axios || !customer || !environment || tab !== 'klantmails') {
            setNotice(null);
            return;
        }

        setNoticeLoading(true);
        setNoticeError(null);

        getIssueNotice(axios, customer, environment, noticeAll)
            .then(setNotice)
            .catch((ex) => setNoticeError(describeError(ex)))
            .finally(() => setNoticeLoading(false));
    }, [axios, customer, environment, noticeAll, tab]);

    const loadUnrecognised = useCallback(() => {
        if (!axios) {
            return;
        }

        setUnrecognisedLoading(true);
        setUnrecognisedError(null);

        getUnrecognised(axios, Number(days), stage)
            .then(setUnrecognised)
            .catch((ex) => setUnrecognisedError(describeError(ex)))
            .finally(() => setUnrecognisedLoading(false));
    }, [axios, days, stage]);

    const loadSweeper = useCallback(() => {
        if (!axios) {
            return;
        }

        getSweeperStatus(axios, stage)
            .then((next) => {
                setSweeper(next);
                setSweeperError(null);
            })
            .catch((ex) => setSweeperError(describeError(ex)));
    }, [axios, stage]);

    const loadSummary = useCallback(() => {
        if (!axios) {
            return;
        }

        setSummaryLoading(true);
        setSummaryError(null);

        getRequeueSummary(axios, stage)
            .then(setSummary)
            .catch((ex) => setSummaryError(describeError(ex)))
            .finally(() => setSummaryLoading(false));
    }, [axios, stage]);

    const loadAttention = useCallback(() => {
        if (!axios) {
            return;
        }

        setAttentionLoading(true);
        setAttentionError(null);

        getAttention(axios, stage)
            .then(setAttention)
            .catch((ex) => setAttentionError(describeError(ex)))
            .finally(() => setAttentionLoading(false));
    }, [axios, stage]);

    useEffect(loadOverview, [loadOverview]);
    useEffect(loadList, [loadList]);
    useEffect(loadNotice, [loadNotice]);
    useEffect(loadSweeper, [loadSweeper]);
    useEffect(() => () => controller.current?.abort(), []);

    // The tabs that read the sweep rows load when someone looks, and again when the stage changes.
    useEffect(() => {
        if (tab === 'voorstellen') {
            loadSummary();
        }
    }, [tab, loadSummary]);

    useEffect(() => {
        if (tab === 'nu') {
            loadAttention();
        }
    }, [tab, loadAttention]);

    // The unknown errors are one scan over the whole backlog; only when someone looks.
    useEffect(() => {
        if (tab === 'onbekend' && unrecognised === null && !unrecognisedLoading) {
            loadUnrecognised();
        }
    }, [tab, unrecognised, unrecognisedLoading, loadUnrecognised]);

    // The search box writes to the URL after a pause, not on every key.
    const written = useRef<string>(q);

    useEffect(() => {
        if (search === q) {
            return;
        }

        const timer = window.setTimeout(() => {
            written.current = search;
            update({ q: search });
        }, 400);

        return () => window.clearTimeout(timer);
    }, [search, q, update]);

    // A search changed elsewhere, by "Alles wissen" or Back, shows in the box too.
    useEffect(() => {
        if (q !== written.current) {
            written.current = q;
            setSearch(q);
        }
    }, [q]);

    /** Everything that changes when issues or proposals do, for the tab on screen. */
    const reloadAfterChange = useCallback(() => {
        loadOverview();
        loadList();

        if (tab === 'voorstellen') {
            loadSummary();
        }

        if (tab === 'nu') {
            loadAttention();
        }
    }, [tab, loadOverview, loadList, loadSummary, loadAttention]);

    // Every few seconds while a round runs, once a minute otherwise.
    const running = sweeper?.round?.state === 'Running' && !sweeper.round.stale;
    const wasRunning = useRef<boolean>(false);

    useEffect(() => {
        const timer = window.setInterval(loadSweeper, running ? 4000 : 60000);

        if (wasRunning.current && !running) {
            reloadAfterChange();
        }

        wasRunning.current = running;

        return () => window.clearInterval(timer);
    }, [running, loadSweeper, reloadAfterChange]);

    // The table of environments follows the scan: each one that is done shows its new counts.
    // Only while someone looks at that table; the rest catches up when the round ends.
    const done = sweeper?.round?.done ?? 0;

    useEffect(() => {
        if (running && done > 0 && tab === 'overzicht') {
            loadOverview();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [done]);

    /** Opens one environment, or goes back to all of them. */
    const select = (e: IssueEnvironment | { customer: string; environment: string } | null, nextTab: Tab | null = null) =>
        go({
            customer: e?.customer ?? null,
            environment: e?.environment ?? null,
            code: null,
            ...(nextTab ? { tab: nextTab } : {}),
        });

    /** A tab of all customers: leaves the environment that was open. */
    const openTab = (id: Tab) => go({ tab: id === 'nu' ? null : id, customer: null, environment: null, code: null });

    /** A dry run over the issues of the environment the page shows, then to its report. */
    const reevaluate = () => {
        if (!axios || !customer || !environment) {
            return;
        }

        setReevaluating(true);
        setError(null);

        startReevaluation(axios, { customer, environment })
            .then(() => go({ tab: 'herbeoordeling', code: null }))
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setReevaluating(false));
    };

    const sweep = (e: IssueEnvironment) => {
        if (!axios) {
            return;
        }

        sweepTarget.current = e;
        setSweepProgress(null);
        setSweeping(environmentKey(e.customer, e.environment));

        sweepNow(axios, e.customer, e.environment)
            .then(() => {
                reloadAfterChange();
                loadNotice();
                loadSweeper();
            })
            .catch((ex) => setError(describeError(ex)))
            .finally(() => {
                sweepTarget.current = null;
                setSweepProgress(null);
                setSweeping(null);
            });
    };

    // While one environment is scanned, ask every second how far it is.
    useEffect(() => {
        const target = sweepTarget.current;

        if (!axios || !sweeping || !target) {
            return;
        }

        const timer = window.setInterval(() => {
            getSweepProgress(axios, target.customer, target.environment)
                .then((next) => {
                    if (sweepTarget.current === target && next) {
                        setSweepProgress(next);
                    }
                })
                .catch(() => undefined);
        }, 1000);

        return () => window.clearInterval(timer);
    }, [axios, sweeping]);

    const changeLanguage = (e: IssueEnvironment, language: string) => {
        if (!axios) {
            return;
        }

        setIssueLanguage(axios, e.customer, e.environment, language)
            .then(() => {
                loadOverview();
                loadNotice();
            })
            .catch((ex) => setError(describeError(ex)));
    };

    const changeScope = (mode: ScanMode, chosen: string[]) => {
        if (!axios) {
            return;
        }

        setSweeperBusy(true);

        saveScanScope(axios, mode, chosen)
            .then(loadSweeper)
            .catch((ex) => setSweeperError(describeError(ex)))
            .finally(() => setSweeperBusy(false));
    };

    const pauseScanner = (paused: boolean) => {
        if (!axios) {
            return;
        }

        setSweeperBusy(true);

        saveScannerPause(axios, paused)
            .then(loadSweeper)
            .catch((ex) => setSweeperError(describeError(ex)))
            .finally(() => setSweeperBusy(false));
    };

    const runScan = (resume: boolean) => {
        if (!axios) {
            return;
        }

        setSweeperBusy(true);

        runSweeper(axios, resume, stage)
            .then(() => window.setTimeout(loadSweeper, 1500))
            .catch((ex) => setSweeperError(describeError(ex)))
            .finally(() => setSweeperBusy(false));
    };

    const stopScan = () => {
        if (!axios) {
            return;
        }

        setSweeperBusy(true);

        stopSweeper(axios)
            .then(() => window.setTimeout(loadSweeper, 1500))
            .catch((ex) => setSweeperError(describeError(ex)))
            .finally(() => setSweeperBusy(false));
    };

    /** Runs a change, shows what it came to, and brings the screen up to date. */
    const act = <T,>(run: () => Promise<T>, describe?: (result: T) => string | null) => {
        if (!axios) {
            return;
        }

        setDeciding(true);
        setDecisionMessage(null);

        run()
            .then((result) => {
                setDecisionMessage(describe ? describe(result) : null);
                reloadAfterChange();
                loadSweeper();
            })
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setDeciding(false));
    };

    /** Closes issues after asking why. */
    const close = async (items: IssueRef[], what: string) => {
        if (!axios || items.length === 0) {
            return;
        }

        const choice = await askClose(what);

        if (choice) {
            act(
                () => closeIssues(axios, items, choice.note, choice.kind),
                (closed) => `${plural(closed, 'issue', 'issues')} gesloten.`
            );
        }
    };

    const reopen = (items: IssueRef[]) => {
        if (!axios || items.length === 0) {
            return;
        }

        act(() => reopenIssues(axios, items));
    };

    /**
     * Closes every open issue of a kind of problem on the server - in the environment chosen, or
     * across them - after showing how many that are.
     */
    const closeAllOf = async (group: IssueGroup, onlyBacklog: boolean) => {
        if (!axios) {
            return;
        }

        const target = {
            customer: customer || null,
            environment: environment || null,
            code: group.code,
            stage,
            olderThanDays: onlyBacklog ? maxAgeDays : null,
        };

        setDeciding(true);

        let count: number;

        try {
            count = await closeMatching(axios, { ...target, dryRun: true });
        } catch (ex) {
            setError(describeError(ex));
            setDeciding(false);
            return;
        }

        setDeciding(false);

        if (count === 0) {
            setDecisionMessage('Er staat niets meer open om te sluiten.');
            return;
        }

        const where = selected ? `${customer} / ${environment}` : group.environments.length === 1 ? group.environments[0] : `${group.environments.length} omgevingen`;
        const choice = await askClose(
            `${plural(count, 'issue', 'issues')} sluiten`,
            `"${group.title}" bij ${where}. ${onlyBacklog ? `Alleen met een eerste fout van langer dan ${maxAgeDays} dagen geleden.` : 'Elke leeftijd.'}`
        );

        if (choice) {
            act(
                () => closeMatching(axios, { ...target, reason: choice.note, kind: choice.kind }),
                (closed) => `${plural(closed, 'issue', 'issues')} gesloten.`
            );
        }
    };

    /** Starts or ends looking into a kind of problem. */
    const investigate = (group: Pick<IssueGroup, 'code' | 'title'>, start: boolean) => {
        if (!axios) {
            return;
        }

        act(
            () =>
                start
                    ? saveInvestigation(axios, { code: group.code, title: group.title, environment: selected ? `${customer}/${environment}` : null }).then(() => null)
                    : stopInvestigation(axios, group.code).then(() => null),
            () => (start ? 'Onderzoek gestart. De gegevens staan op het klembord: plak ze in Claude Code.' : 'Onderzoek afgerond.')
        );
    };

    const decide = (approve: boolean, items: IssueRef[]) => {
        if (!axios) {
            return;
        }

        setDecisions(null);

        act(
            () => (approve ? approveProposals(axios, items) : rejectProposals(axios, items).then(() => null)),
            (results) => {
                setDecisions(results);

                return approve ? null : `${plural(items.length, 'voorstel', 'voorstellen')} afgewezen.`;
            }
        );
    };

    const decideGroup = (approve: boolean, group: RequeueGroupRef, count: number) => {
        if (!axios) {
            return;
        }

        const what = `${plural(count, 'voorstel', 'voorstellen')} van ${group.customer} / ${group.environment}`;
        const question = approve
            ? `${what} goedkeuren?\n\nDe scanner biedt ze aan in porties van hooguit ${summary?.maxPerRound ?? 20} per ronde. Alleen als Exact ruimte heeft.`
            : `${what} afwijzen?\n\nZe komen pas terug als het object opnieuw faalt.`;

        if (!window.confirm(question)) {
            return;
        }

        setDecisions(null);

        act(
            () => (approve ? approveGroup(axios, group) : rejectGroup(axios, group)),
            (n) => (approve ? `${plural(n, 'voorstel', 'voorstellen')} goedgekeurd. Ze gaan mee in de volgende porties.` : `${plural(n, 'voorstel', 'voorstellen')} afgewezen.`)
        );
    };

    const closeProposalGroup = async (group: ProposalGroup) => {
        if (!axios) {
            return;
        }

        const choice = await askClose(
            `${plural(group.count, 'issue', 'issues')} sluiten`,
            `"${group.title}" bij ${group.customer} / ${group.environment}. Ze worden niet opnieuw aangeboden.`
        );

        if (!choice) {
            return;
        }

        act(
            () =>
                closeMatching(axios, {
                    customer: group.customer,
                    environment: group.environment,
                    code: group.code,
                    olderThanDays: group.backlog ? maxAgeDays : null,
                    proposedOnly: true,
                    reason: choice.note,
                    kind: choice.kind,
                }),
            (closed) => `${plural(closed, 'issue', 'issues')} gesloten.`
        );
    };

    const changeMode = (mode: AutoRequeueMode | null) => {
        if (!axios) {
            return;
        }

        act(
            () => saveAutoRequeueMode(axios, mode),
            () => 'Opgeslagen. Geldt vanaf de volgende scan van elke omgeving.'
        );
    };

    const markSent = (target: IssueNotice, sent: NoticeSent) => {
        if (!axios) {
            return;
        }

        setMarking(true);

        markNoticeSent(axios, target.customer, target.environment, sent)
            .then(() => {
                loadNotice();
                loadList();
                loadOverview();
            })
            .catch((ex) => setNoticeError(describeError(ex)))
            .finally(() => setMarking(false));
    };

    const setStage = (next: Stage | '') => {
        writeStage(next);
        setUnrecognised(null);
        update({ stage: next || null });
    };

    /** Where an attention point leads: the tab, filtered to what it is about. */
    const openItem = (item: AttentionItem) =>
        go({
            tab: item.tab === 'nu' ? null : item.tab,
            customer: item.customer ?? null,
            environment: item.environment ?? null,
            code: item.tab === 'issues' ? item.code ?? null : null,
            status: item.tab === 'issues' ? item.status ?? 'open' : null,
            owner: null,
            q: null,
            age: item.tab === 'issues' ? 'all' : null,
        });

    /** The issues of a kind of problem, in one environment ("customer/environment") or across them. */
    const openProblem = (problem: string, where?: string | null) => {
        const [c, e] = (where ?? '').split('/');

        go({ tab: 'issues', customer: c || null, environment: e || null, code: problem, status: 'open', age: 'all', owner: null, q: null });
    };

    /** The issues of a kind of problem that wait on us, in its one environment or across them; without a kind, all of the environment's. */
    const openOurs = (ours: AttentionOurs) => {
        const [c, e] = ours.environments.length === 1 ? ours.environments[0].split('/') : [];

        go({ tab: 'issues', customer: c || null, environment: e || null, code: ours.code ?? null, status: 'WaitingOnUs', age: 'all', owner: null, q: null });
    };

    const openMail = (mail: AttentionMail) => go({ tab: 'klantmails', customer: mail.customer, environment: mail.environment, code: null });

    /** Puts every filter back to how the list starts; the environment stays. */
    const clearFilters = () => {
        written.current = '';
        setSearch('');
        update({ status: null, age: null, owner: null, code: null, q: null, days: null });
    };

    // The environments of the chosen stage; everything on the page counts from these.
    const visible = useMemo(() => (environments ?? []).filter((e) => !stage || e.stage === stage), [environments, stage]);

    const totals = useMemo(
        () => ({
            // One mail per environment whose customer has something new to hear.
            mails: visible.filter((e) => e.customerNotTold > 0).length,
            proposals: visible.reduce((sum, e) => sum + e.proposalsWaiting, 0),
        }),
        [visible]
    );

    const urgent = attention?.items.filter((i) => i.severity !== 'info').length;

    const counts: Partial<Record<Tab, number>> = {
        nu: urgent,
        voorstellen: totals.proposals,
        klantmails: totals.mails,
        onbekend: unrecognised?.length,
    };

    const current = useMemo(
        () => (selected ? (environments ?? []).find((e) => environmentKey(e.customer, e.environment) === selected) ?? null : null),
        [environments, selected]
    );

    // The environments whose customer waits on a mail first, for the picker on the mail tab.
    const mailable = useMemo(
        () =>
            [...visible].sort(
                (a, b) => b.customerNotTold - a.customerNotTold || b.waitingOnCustomer - a.waitingOnCustomer || a.customer.localeCompare(b.customer)
            ),
        [visible]
    );

    /** The filters that differ from how the list starts, each with a way to take it off. */
    const activeFilters: { key: string; label: string; clear: () => void }[] = [
        !inEnvironment && selected ? { key: 'env', label: `${customer} / ${environment}`, clear: () => select(null) } : null,
        status !== 'open' ? { key: 'status', label: `Status: ${STATUSES[status] ?? status}`, clear: () => update({ status: null }) } : null,
        age !== 'recent' && status !== 'resolved' ? { key: 'age', label: `Leeftijd: ${AGES[age || 'all']}`, clear: () => update({ age: null }) } : null,
        code ? { key: 'code', label: `Probleem: ${list?.groups.find((g) => g.code === code)?.title ?? code}`, clear: () => update({ code: null }) } : null,
        owner ? { key: 'owner', label: `Eigenaar: ${OWNER_LABEL[owner] ?? owner}`, clear: () => update({ owner: null }) } : null,
        q
            ? {
                  key: 'q',
                  label: `Zoekt: "${q}"`,
                  clear: () => {
                      written.current = '';
                      setSearch('');
                      update({ q: null });
                  },
              }
            : null,
        days !== DEFAULT_DAYS && (status === 'resolved' || status === 'all') ? { key: 'days', label: `Periode: ${PERIODS[days] ?? `${days} dagen`}`, clear: () => update({ days: null }) } : null,
    ].filter((f): f is { key: string; label: string; clear: () => void } => f !== null);

    if (identity === 'ready' && !isAdmin) {
        return (
            <main className="issues">
                <p className="issues__muted">Deze pagina is alleen voor beheerders.</p>
            </main>
        );
    }

    return (
        <main className="issues">
            {axios && <RelinkDialog axios={axios} issue={relinking} onClose={() => setRelinking(null)} onDone={reloadAfterChange} />}
            {axios && <LinkDialog axios={axios} issue={linking} onClose={() => setLinking(null)} onDone={reloadAfterChange} />}
            {closeDialog}

            {inEnvironment ? (
                <nav className="issues__crumbs" aria-label="Waar je bent">
                    <button type="button" className="btn btn-sm btn-link" onClick={() => select(null, tab === 'klantmails' ? 'klantmails' : 'issues')}>
                        <FontAwesomeIcon icon="arrow-left" /> Alle klanten
                    </button>
                    <FontAwesomeIcon icon="angle-right" className="issues__crumbs-sep" />
                    <strong>
                        {customer} / {environment}
                    </strong>
                </nav>
            ) : (
                <header className="issues__banner">
                    <div>
                        <h2 className="issues__title">Issues</h2>
                        <p>Alles wat bij de klanten misging. De scanner werkt het elk kwartier bij. Begin bij Nu kijken.</p>
                    </div>
                    <div className="issues__stages" role="group" aria-label="Omgevingen">
                        {STAGES.map(({ stage: id, label }) => (
                            <button
                                key={id || 'all'}
                                type="button"
                                className={`issues__stage${stage === id ? ' is-active' : ''}`}
                                onClick={() => setStage(id)}
                                aria-pressed={stage === id}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </header>
            )}

            {inEnvironment &&
                (current ? (
                    <EnvironmentCard
                        environment={current}
                        sweeping={sweeping === selected}
                        progress={sweeping === selected ? sweepProgress : null}
                        onSweep={() => sweep(current)}
                        onMail={() => go({ tab: 'klantmails' })}
                    />
                ) : (
                    environments === null && <LoadingBlock compact label="Omgeving ophalen…" />
                ))}

            <nav className="issues__tabs" role="tablist" aria-label="Onderdelen">
                {inEnvironment
                    ? ENVIRONMENT_TABS.map((id) => {
                          const count = id === 'klantmails' ? current?.customerNotTold ?? 0 : 0;

                          return (
                              <button
                                  key={id}
                                  type="button"
                                  role="tab"
                                  aria-selected={tab === id}
                                  className={`issues__tab${tab === id ? ' is-active' : ''}`}
                                  onClick={() => go({ tab: id })}
                              >
                                  <FontAwesomeIcon icon={id === 'issues' ? 'list-ul' : 'envelope'} /> {id === 'issues' ? 'Issues' : 'Klantmail'}
                                  {count > 0 && <span className="issues__tab-count">{count.toLocaleString('nl-NL')}</span>}
                              </button>
                          );
                      })
                    : TABS.map(({ tab: id, label, icon }) => (
                          <button
                              key={id}
                              type="button"
                              role="tab"
                              aria-selected={tab === id}
                              className={`issues__tab${tab === id ? ' is-active' : ''}`}
                              onClick={() => openTab(id)}
                          >
                              <FontAwesomeIcon icon={icon} /> {label}
                              {(counts[id] ?? 0) > 0 && (
                                  <span className={`issues__tab-count${id === 'nu' ? ' issues__tab-count--alert' : ''}`}>{(counts[id] ?? 0).toLocaleString('nl-NL')}</span>
                              )}
                              {id === 'overzicht' && running && <FontAwesomeIcon icon="rotate" spin className="issues__tab-spin" />}
                          </button>
                      ))}
            </nav>

            {error && <p className="issues__error">{error}</p>}
            {decisionMessage && tab !== 'voorstellen' && <p className="proposals__message">{decisionMessage}</p>}

            {tab === 'nu' && (
                <AttentionPanel
                    attention={attention}
                    loading={attentionLoading}
                    error={attentionError}
                    nextScanAt={sweeper?.round?.nextRunAt}
                    onOpen={openItem}
                    onProposals={() => openTab('voorstellen')}
                    onBacklog={() => go({ tab: 'issues', status: 'open', owner: null, age: 'backlog', customer: null, environment: null, code: null })}
                    onRecent={() => go({ tab: 'issues', status: 'open', owner: null, age: null, customer: null, environment: null, code: null })}
                    onMail={openMail}
                    onProblem={openProblem}
                    onOurs={openOurs}
                    onAllOurs={() => go({ tab: 'issues', status: 'WaitingOnUs', owner: null, age: 'all', customer: null, environment: null, code: null, q: null })}
                    onStopInvestigation={(i: IssueInvestigation) => investigate({ code: i.code, title: i.title ?? i.code }, false)}
                    before={
                        <IncidentsPanel
                            stage={stage}
                            onOpenIssue={(i) => go({ tab: 'issues', customer: i.customer, environment: i.environment, q: i.relationId, status: 'all', age: 'all', code: null, owner: null })}
                        />
                    }
                />
            )}

            {tab === 'overzicht' && (
                <>
                    <SweeperPanel
                        status={sweeper}
                        error={sweeperError}
                        busy={sweeperBusy}
                        stage={stage}
                        environments={environments ?? []}
                        onRun={runScan}
                        onStop={stopScan}
                        onScope={changeScope}
                        onPause={pauseScanner}
                        onProposals={() => openTab('voorstellen')}
                    />

                    <section className="issues__section" aria-label="Per omgeving">
                        <h3 className="issues__heading">
                            <FontAwesomeIcon icon="table" /> Per omgeving
                        </h3>
                        <p className="issues__muted">Klik een omgeving om haar issues te zien.</p>
                        {environments === null ? (
                            <LoadingBlock label="Omgevingen ophalen…" />
                        ) : (
                            <Refreshing active={overviewLoading} label="Tellingen bijwerken…">
                                <IssueOverviewTable
                                    environments={visible}
                                    selected={selected}
                                    sweeping={sweeping}
                                    onSelect={(e) => select(e, e ? 'issues' : null)}
                                    onSweep={sweep}
                                />
                            </Refreshing>
                        )}
                    </section>
                </>
            )}

            {tab === 'herbeoordeling' && (
                <ReevaluationPanel
                    environments={environments ?? []}
                    stage={stage}
                    initialCustomer={customer}
                    initialEnvironment={environment}
                    onApplied={loadOverview}
                />
            )}

            {tab === 'issues' && (
                <>
                    <StatusFilter
                        counts={list?.counts ?? null}
                        status={status}
                        age={age}
                        maxAgeDays={maxAgeDays}
                        onStatus={(next) => update({ status: next === 'open' ? null : next })}
                        onAge={(next) => update({ age: next === 'recent' ? null : next || 'all' })}
                    />

                    <div className="issues__filters" role="group" aria-label="Zoeken">
                        <input
                            className="form-control form-control-sm issues__search"
                            type="search"
                            placeholder="Zoek op factuur, klant of fouttekst"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            aria-label="Zoeken"
                        />
                        {(status === 'resolved' || status === 'all') && (
                            <label className="issues__period">
                                <span>Opgelost in de laatste</span>
                                <select className="form-select form-select-sm" value={days} onChange={(event) => update({ days: event.target.value })} aria-label="Periode">
                                    {Object.entries(PERIODS).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {selected && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={reevaluate}
                                disabled={reevaluating}
                                title={`Proefronde over alle issues van ${customer} / ${environment}; verandert niets tot Uitvoeren op Herbeoordeling.`}
                            >
                                <FontAwesomeIcon icon={reevaluating ? 'spinner' : 'scale-balanced'} spin={reevaluating} /> Herbeoordelen
                            </button>
                        )}
                    </div>

                    {activeFilters.length > 0 && (
                        <div className="issues__active" role="group" aria-label="Actieve filters">
                            <span className="issues__active-label">
                                <FontAwesomeIcon icon="filter" /> Filter
                            </span>
                            {activeFilters.map((f) => (
                                <span key={f.key} className="issues__chip">
                                    {f.label}
                                    <button type="button" onClick={f.clear} aria-label={`${f.label} weghalen`}>
                                        <FontAwesomeIcon icon="xmark" />
                                    </button>
                                </span>
                            ))}
                            <button type="button" className="btn btn-sm btn-link" onClick={clearFilters}>
                                Alles wissen
                            </button>
                        </div>
                    )}

                    {list ? (
                        <IssueBacklog
                            list={list}
                            activeCode={code}
                            onCode={(next) => update({ code: next })}
                            loading={loading}
                            busy={deciding}
                            oneEnvironment={!!selected}
                            onClose={close}
                            onReopen={reopen}
                            onRelink={setRelinking}
                            onLink={setLinking}
                            onMail={(i) => go({ tab: 'klantmails', customer: i.customer, environment: i.environment, code: null })}
                            age={age}
                            maxAgeDays={maxAgeDays}
                            onCloseMatching={closeAllOf}
                            onInvestigate={investigate}
                            scanner={sweeper}
                        />
                    ) : (
                        <LoadingBlock label="Issues ophalen…" />
                    )}
                </>
            )}

            {tab === 'voorstellen' && (
                <ProposalsPanel
                    axios={axios}
                    summary={summary}
                    loading={summaryLoading}
                    error={summaryError}
                    status={sweeper}
                    busy={deciding}
                    message={decisionMessage}
                    results={decisions}
                    onMode={changeMode}
                    onApproveGroup={(group, count) => decideGroup(true, group, count)}
                    onRejectGroup={(group, count) => decideGroup(false, group, count)}
                    onCloseGroup={closeProposalGroup}
                    onApprove={(items) => decide(true, items)}
                    onReject={(items) => decide(false, items)}
                />
            )}

            {tab === 'klantmails' && (
                <>
                    <div className="issues__filters" role="group" aria-label="Omgeving">
                        {inEnvironment && current ? (
                            <label className="issues__period">
                                <span>Taal van de klant</span>
                                <select className="form-select form-select-sm" value={current.language} onChange={(event) => changeLanguage(current, event.target.value)}>
                                    {Object.entries(LANGUAGES).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        ) : (
                            <select
                                className="form-select form-select-sm"
                                value={selected ?? ''}
                                onChange={(event) => select(mailable.find((e) => environmentKey(e.customer, e.environment) === event.target.value) ?? null, 'klantmails')}
                                aria-label="Omgeving"
                            >
                                <option value="">Kies een omgeving…</option>
                                {mailable.map((e) => (
                                    <option key={environmentKey(e.customer, e.environment)} value={environmentKey(e.customer, e.environment)}>
                                        {e.customer} / {e.environment}
                                        {e.customerNotTold > 0 ? ` (${e.customerNotTold} nieuw)` : e.waitingOnCustomer > 0 ? ` (${e.waitingOnCustomer} wacht op klant)` : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {selected ? (
                        <IssueNoticePanel
                            notice={notice}
                            loading={noticeLoading}
                            error={noticeError}
                            all={noticeAll}
                            marking={marking}
                            onAllChange={setNoticeAll}
                            onMarkSent={markSent}
                        />
                    ) : (
                        <p className="issues__muted">Kies een omgeving om de mail aan de klant op te stellen.</p>
                    )}

                    <NoticeStylePanel axios={axios} />
                </>
            )}

            {tab === 'onbekend' && (
                <UnrecognisedPanel
                    errors={unrecognised}
                    loading={unrecognisedLoading}
                    error={unrecognisedError}
                    onLoad={loadUnrecognised}
                    onShowIssues={(next) => go({ tab: 'issues', code: next, status: 'all', owner: null, customer: null, environment: null, q: null, age: 'all' })}
                />
            )}
        </main>
    );
};
